// Setup › Price book's item, Blueprint (pages/ux-full setup/pricebook, the item's History): every change
// kept, newest first, drawn by ui-web's ItemHistory; "Put back these values" on an entry asks why, then
// puts that save's old values back as a new change (pricebook.rollback). The server decides who may and
// leaves our cost out for whoever doesn't see it; a refusal shows its words.
import { useEffect, useState } from 'react';
import { ItemHistory, type ItemChange, type ItemHistoryProps } from '@flowology/ui-web';
import '@flowology/ui-web/base.css';
import { client } from '../api.ts';
import { refusal } from '../history/customer-edit-model.ts';
import { useWrite } from '../shared/EditSheet.tsx';
import {
  HISTORY_PAGE,
  morePage,
  putBackDone,
  putBackInput,
  putBackLines,
  REASON_MAX,
} from './item-history-model.ts';
import './pricebook-history.css';

export function PricebookHistory({
  item,
  timeZone,
  canPutBack,
  onPutBack,
}: {
  /** The item as the sheet has it: a put-back goes against its row version. */
  item: { id: string; rowVersion: number };
  timeZone: string;
  /** Price book authority, on an item that may be changed here (not ServiceTitan's while mirroring). */
  canPutBack: boolean;
  /** Values were put back: the sheet reads the item again. */
  onPutBack: () => void;
}) {
  const [page, setPage] = useState<ItemHistoryProps | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [asking, setAsking] = useState<ItemChange[] | null>(null);
  const [reason, setReason] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const w = useWrite(`${item.id}:${asking?.[0]?.id ?? ''}:${reason}`);
  const read = (cursor?: string) =>
    client.call<ItemHistoryProps>('pricebook.itemHistory', {
      itemId: item.id,
      limit: HISTORY_PAGE,
      ...(cursor ? { cursor } : {}),
    });
  // again whenever the item moves on (a save, a put-back): its newest change is then on top
  useEffect(() => {
    let current = true;
    read()
      .then((p) => {
        if (!current) return;
        setPage(p);
        setError(null);
      })
      .catch((e: unknown) => current && setError(refusal(e)));
    return () => {
      current = false;
    };
  }, [item.id, item.rowVersion]);
  const more = async () => {
    if (!page?.next) return;
    try {
      setPage(morePage(page, await read(page.next)));
    } catch (e) {
      setError(refusal(e));
    }
  };
  const putBack = async () => {
    if (!asking) return;
    const r = await w.run<{ restored: string[]; kept: { field: string; why: 'cost' | 'not_by_hand' }[] }>(
      'pricebook.rollback',
      putBackInput(item, asking[0]!.id, reason),
    );
    if (!r) return;
    setDone(putBackDone(r));
    setAsking(null);
    setReason('');
    onPutBack();
  };
  return (
    // a part of the item's sheet, not a zone of its own (a div, with its heading as its name)
    <div className="pb-history flw flw-office" role="region" aria-labelledby="pb-history-title">
      <h4 id="pb-history-title">History</h4>
      {done && (
        <p className="notice ok" role="status">
          {done}
        </p>
      )}
      {asking && (
        <div className="pb-putback" role="group" aria-label="Put back these values">
          <p className="small">Put back, as a new change in its history:</p>
          <ul className="pb-putback-lines">
            {putBackLines(asking).map((l) => (
              <li key={l}>{l}</li>
            ))}
          </ul>
          <label className="f">
            Why
            <input
              className="field"
              value={reason}
              maxLength={REASON_MAX}
              placeholder="Raised the wrong item"
              onChange={(e) => {
                setReason(e.target.value);
                w.setError(null);
              }}
              autoFocus
            />
          </label>
          <p className="muted small">Estimates already sent keep their prices.</p>
          {w.error && <p className="error">{w.error}</p>}
          <div className="row">
            <button className="btn primary" disabled={w.busy} onClick={() => void putBack()}>
              {w.busy ? 'Putting back…' : 'Put them back'}
            </button>
            <button
              className="flw-btn"
              onClick={() => {
                setAsking(null);
                setReason('');
                w.setError(null);
              }}
            >
              Keep them
            </button>
          </div>
        </div>
      )}
      {error && <p className="error">{error}</p>}
      {!page && !error && <p className="muted small">Reading its history…</p>}
      {page && (
        <ItemHistory
          data={page}
          timeZone={timeZone}
          busy={w.busy ? (asking?.[0]?.id ?? null) : null}
          {...(canPutBack
            ? {
                onPutBack: (_: string, entry: ItemChange[]) => {
                  setAsking(entry);
                  setDone(null);
                },
              }
            : {})}
          {...(page.next ? { onMore: () => void more() } : {})}
        />
      )}
    </div>
  );
}
